import { Skeleton } from '@freighthub/silhouette-core'
import { CurrencyCode, getLineItemsSortedByType, IQuoteOption, LineItemInterface } from 'features/BookingFlow'
import { sum } from 'lodash-es'
import { FC, ReactElement } from 'react'
import { UnleashFeatureFlagKeys, useUnleashFeatureFlags } from 'service/useUnleashFeatureFlags'

import { useCurrencyConversionRatesQuery } from './useCurrencyConversionRatesQuery'
import { useLocalCurrency } from './useLocalCurrency'

type CurrencyValueAndExchangeRate = {
  value: number
  exchangeRate: number
}

function convertCurrency(valueAndExchangeRate: CurrencyValueAndExchangeRate): number {
  return valueAndExchangeRate.exchangeRate * valueAndExchangeRate.value
}

export function differentCurrenciesTotal(valuesAndExchangeRates: CurrencyValueAndExchangeRate[]): number {
  return sum(valuesAndExchangeRates.map(convertCurrency))
}

type Currency = { code: CurrencyCode; value: number }

export type FromCurrency = Currency & { currencyExchangeRateId: string }

type CurrencyConversionPropsBase = {
  renderCurrency: (toCurrency: Currency) => ReactElement
}

type SingleCurrencyConversionProps = CurrencyConversionPropsBase & {
  fromCurrency: FromCurrency
}

type MultipleCurrencyConversionProps = CurrencyConversionPropsBase & {
  fromCurrency: FromCurrency[]
}

export type CurrencyConversionProps = SingleCurrencyConversionProps | MultipleCurrencyConversionProps

export const testIds = {
  progressBar: 'PROGRESS_BAR',
}

export const CurrencyConversion: FC<CurrencyConversionProps> = ({ renderCurrency, ...props }) => {
  const { localCurrency } = useLocalCurrency()

  let fromCurrencies: FromCurrency[]
  if (Array.isArray(props.fromCurrency)) {
    fromCurrencies = props.fromCurrency
  } else {
    fromCurrencies = [props.fromCurrency]
  }

  const results = useCurrencyConversionRatesQuery(
    fromCurrencies.map((fromCurrency) => ({
      fromCurrencyCode: fromCurrency.code,
      toCurrencyCode: localCurrency,
      currencyExchangeRateId: fromCurrency.currencyExchangeRateId,
    }))
  )

  const isLoading = results.some((result) => result.isLoading)

  if (isLoading) {
    return <Skeleton data-testid={testIds.progressBar} />
  }

  const total = differentCurrenciesTotal(
    fromCurrencies.map((fromCurrency, index) => ({
      value: fromCurrency.value,
      exchangeRate: results[index].data as number,
    }))
  )

  return renderCurrency({ code: localCurrency, value: total })
}

type CurrencyConversionWithFeatureFlagProps = CurrencyConversionProps & {
  fallbackCurrency: Currency
}

export const CurrencyConversionWithFeatureFlag: FC<CurrencyConversionWithFeatureFlagProps> = ({
  fallbackCurrency,
  ...props
}) => {
  const { isEnabled } = useUnleashFeatureFlags()

  const isCurrencyConversionEnabled = isEnabled(UnleashFeatureFlagKeys.LOCAL_CURRENCY_DISPLAY)
  return isCurrencyConversionEnabled ? <CurrencyConversion {...props} /> : props.renderCurrency(fallbackCurrency)
}

export const getFromCurrencyFromLineItem = (lineItem: LineItemInterface): FromCurrency => ({
  code: lineItem.unitPrice.code as CurrencyCode,
  value: lineItem.unitPrice.value * lineItem.quantity,
  currencyExchangeRateId: lineItem.currencyExchangeRateId,
})

export const getFromCurrencyFromQuoteOption = (quoteOption: IQuoteOption): FromCurrency[] =>
  getLineItemsSortedByType(quoteOption).map((lineItem) => getFromCurrencyFromLineItem(lineItem))
